/**
 * Internationalization (i18n) module for Papelitos.
 * Stores language preference in localStorage, provides t() lookup.
 * Language switcher UI is rendered by theme.ts alongside the theme switcher.
 */

import { en, TranslationKey } from './en';
import { es } from './es';
import { cardsEn } from './cards-en';
import { cardsEs } from './cards-es';

export type Locale = 'en' | 'es';

const STORAGE_KEY = 'papelitos-lang';
const DEFAULT_LOCALE: Locale = 'en';

const translations: Record<Locale, Record<TranslationKey, string>> = {
  en,
  es,
};

const cardTranslations: Record<Locale, Record<string, string>> = {
  en: cardsEn,
  es: cardsEs,
};

let currentLocale: Locale = DEFAULT_LOCALE;

/**
 * Get the current locale.
 */
export function getLocale(): Locale {
  return currentLocale;
}

/**
 * Set the active locale and persist to localStorage.
 */
export function setLocale(locale: Locale): void {
  currentLocale = locale;
  localStorage.setItem(STORAGE_KEY, locale);
  document.documentElement.setAttribute('lang', locale);
}

/**
 * Translate a key, with optional interpolation of {0}, {1}, {2}... placeholders.
 */
export function t(key: TranslationKey, ...args: (string | number)[]): string {
  const dict = translations[currentLocale] || translations[DEFAULT_LOCALE];
  let str = dict[key] ?? en[key] ?? key;

  // Replace {0}, {1}, etc. with provided args
  for (let i = 0; i < args.length; i++) {
    str = str.replace(`{${i}}`, String(args[i]));
  }

  return str;
}

/**
 * Get translated round name by round number.
 */
export function getRoundName(round: number): string {
  return t(`roundName${round}` as TranslationKey);
}

/**
 * Get translated round description by round number.
 */
export function getRoundDesc(round: number): string {
  return t(`roundDesc${round}` as TranslationKey);
}

/**
 * Translate a card description by card ID.
 * Falls back to the provided original description if no translation exists.
 */
export function tCard(cardId: string, fallback: string): string {
  const dict = cardTranslations[currentLocale] || cardTranslations[DEFAULT_LOCALE];
  return dict[cardId] ?? fallback;
}

/**
 * Translate a deck description by deck ID.
 * Falls back to the provided original description if no translation exists.
 */
export function tDeck(deckId: string, fallback: string): string {
  const key = `deck-${deckId}`;
  const dict = cardTranslations[currentLocale] || cardTranslations[DEFAULT_LOCALE];
  return dict[key] ?? fallback;
}

/**
 * Initialize i18n. Call at app startup (before theme init).
 */
export function initI18n(): void {
  const stored = localStorage.getItem(STORAGE_KEY) as Locale | null;
  if (stored && (stored === 'en' || stored === 'es')) {
    currentLocale = stored;
  } else {
    // Try to detect from browser
    const browserLang = navigator.language.split('-')[0];
    if (browserLang === 'es') {
      currentLocale = 'es';
    }
  }
  document.documentElement.setAttribute('lang', currentLocale);
}

export type { TranslationKey };
